import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth, getProviders } from '../lib/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiUser, FiArrowRight, FiTool, FiShield, FiLoader } = FiIcons;

const inputClass =
  'w-full bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 pl-11 pr-4 py-3 text-sm outline-none focus:border-blue-600 text-slate-900 dark:text-white';
const labelClass = 'caption text-left block';
const primaryButton =
  'btn-primary w-full !py-3';

const Field = ({ label, icon, ...props }) => (
  <div className="space-y-1.5">
    <label className={labelClass}>{label}</label>
    <div className="relative">
      <SafeIcon icon={icon} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className={inputClass} {...props} />
    </div>
  </div>
);

/**
 * Sign-in / sign-up card. Which methods appear is decided by NoCodeBackend:
 * /api/auth-providers is read on mount, so enabling or disabling Google, Apple,
 * Email OTP, or public signup in the NCB dashboard changes this screen with no code change.
 */
const AuthCard = ({ mode = 'signin' }) => {
  const { user, loading: authLoading, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const [providers, setProviders] = useState(null);
  const [view, setView] = useState('main'); // main | otp | forgot
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '', otp: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const isSignup = mode === 'signup';

  useEffect(() => {
    let cancelled = false;
    getProviders()
      .then((data) => !cancelled && setProviders(data.providers || {}))
      .catch(() => !cancelled && setProviders({ email: true, public_signup: true }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!authLoading && user) return <Navigate to={from} replace />;

  if (!providers) {
    return (
      <div className="flex items-center justify-center gap-3 p-10 text-slate-500 text-xs font-bold uppercase tracking-widest">
        <SafeIcon icon={FiLoader} className="animate-spin" /> Loading sign-in options...
      </div>
    );
  }

  const run = async (fn) => {
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const finishSignIn = async (dest) => {
    const signedIn = await refreshSession();
    if (signedIn) navigate(dest, { replace: true });
    else setInfo('Account created. Check your email to verify your address, then sign in.');
  };

  const handleEmailAuth = (e) => {
    e.preventDefault();
    run(async () => {
      if (isSignup) {
        if (form.password !== form.confirm) throw new Error("Passwords don't match");
        await auth('sign-up/email', { email: form.email, password: form.password, name: form.name });
        await finishSignIn('/pricing');
      } else {
        await auth('sign-in/email', { email: form.email, password: form.password });
        await finishSignIn(from);
      }
    });
  };

  const handleSocial = (provider) =>
    run(async () => {
      const data = await auth('sign-in/social', {
        provider,
        callbackURL: `${window.location.origin}/auth/callback`,
      });
      if (!data?.url) throw new Error('Social sign-in is unavailable right now');
      window.location.href = data.url;
    });

  const handleSendOtp = (e) => {
    e.preventDefault();
    run(async () => {
      await auth('email-otp/send-verification-otp', { email: form.email, type: 'sign-in' });
      setOtpSent(true);
      setInfo('We emailed you a 6-digit code.');
    });
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    run(async () => {
      await auth('sign-in/email-otp', { email: form.email, otp: form.otp });
      await finishSignIn(from);
    });
  };

  const handleForgot = (e) => {
    e.preventDefault();
    run(async () => {
      await auth('request-password-reset', {
        email: form.email,
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setInfo('If an account exists for that email, a reset link is on its way.');
    });
  };

  const hasSocial = providers.google || providers.apple;
  const nothingEnabled = !providers.email && !providers.emailOTP && !hasSocial;
  const signupBlocked = isSignup && providers.public_signup === false;

  const title =
    view === 'forgot' ? 'Reset password' : view === 'otp' ? 'Passwordless sign in' : isSignup ? 'Join PartScouter' : null;
  const switchView = (next) => {
    setView(next);
    setError(null);
    setInfo(null);
    setOtpSent(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-100 dark:bg-slate-950">
      <div className="w-full max-w-md space-y-6">
        <div className="text-left">
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="bg-blue-600 p-2">
              <SafeIcon icon={FiTool} className="w-5 h-5 text-white" />
            </div>
            <span className="font-cond text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Appliance parts catalog</span>
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">
            {title || (
              <>
                Part<span className="text-blue-500">Scouter</span>
              </>
            )}
          </h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            {isSignup ? 'Create an account to search the master parts database.' : 'Sign in to look up parts and cross-references.'}
          </p>
        </div>

        <div className="bg-white dark:bg-slate-900 p-8 border border-slate-300 dark:border-slate-800 border-t-[3px] border-t-blue-600 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}
          {info && (
            <div className="p-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-sm text-blue-800 dark:text-blue-300">
              {info}
            </div>
          )}

          {nothingEnabled && (
            <p className="text-sm text-slate-500 text-center">Sign-in is currently disabled. Please check back soon.</p>
          )}
          {signupBlocked && (
            <p className="text-sm text-slate-500 text-center">Public registration is restricted by the administrator.</p>
          )}

          {/* Social providers (only the ones enabled in NoCodeBackend) */}
          {hasSocial && view === 'main' && !signupBlocked && (
            <div className="grid grid-cols-1 gap-3">
              {providers.google && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => handleSocial('google')}
                  className="btn-secondary w-full !py-3"
                >
                  Continue with Google
                </button>
              )}
              {providers.apple && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => handleSocial('apple')}
                  className="px-4 py-3 bg-black text-white text-sm font-semibold hover:bg-neutral-900"
                >
                  Continue with Apple
                </button>
              )}
              {(providers.email || providers.emailOTP) && (
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-100 dark:border-slate-800" />
                  </div>
                  <div className="relative flex justify-center caption">
                    <span className="bg-white dark:bg-slate-900 px-3 tracking-widest">or</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email + password */}
          {providers.email && view === 'main' && !signupBlocked && (
            <form onSubmit={handleEmailAuth} className="space-y-4">
              {isSignup && (
                <Field label="Full Name" icon={FiUser} required placeholder="Full name" value={form.name} onChange={set('name')} autoComplete="name" />
              )}
              <Field label="Work Email" icon={FiMail} type="email" required placeholder="name@company.com" value={form.email} onChange={set('email')} autoComplete="email" />
              <div className="space-y-1.5">
                {!isSignup && (
                  <div className="flex justify-end -mb-1">
                    <button type="button" onClick={() => switchView('forgot')} className="text-xs font-semibold text-blue-700 hover:underline">
                      Forgot?
                    </button>
                  </div>
                )}
                <Field label="Password" icon={FiLock} type="password" required minLength={isSignup ? 8 : undefined} placeholder="••••••••" value={form.password} onChange={set('password')} autoComplete={isSignup ? 'new-password' : 'current-password'} />
              </div>
              {isSignup && (
                <Field label="Confirm Password" icon={FiLock} type="password" required placeholder="••••••••" value={form.confirm} onChange={set('confirm')} autoComplete="new-password" />
              )}
              <button type="submit" disabled={submitting} className={primaryButton}>
                {submitting ? 'Please wait…' : (
                  <>
                    {isSignup ? 'Create account' : 'Sign in'} <SafeIcon icon={FiArrowRight} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Email OTP (passwordless) */}
          {providers.emailOTP && view === 'otp' && (
            <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className="space-y-4">
              <Field label="Work Email" icon={FiMail} type="email" required placeholder="name@company.com" value={form.email} onChange={set('email')} disabled={otpSent} />
              {otpSent && (
                <Field label="6-digit code" icon={FiLock} required maxLength={6} inputMode="numeric" placeholder="123456" value={form.otp} onChange={set('otp')} autoComplete="one-time-code" />
              )}
              <button type="submit" disabled={submitting} className={primaryButton}>
                {submitting ? 'Please wait…' : otpSent ? 'Verify and sign in' : 'Send code'}
              </button>
            </form>
          )}

          {/* Forgot password (required whenever email + password is enabled) */}
          {providers.email && view === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-4">
              <Field label="Work Email" icon={FiMail} type="email" required placeholder="name@company.com" value={form.email} onChange={set('email')} autoComplete="email" />
              <button type="submit" disabled={submitting} className={primaryButton}>
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          )}

          {view !== 'main' && (
            <button type="button" onClick={() => switchView('main')} className="w-full text-xs font-semibold text-blue-700 hover:underline">
              ← Back to sign in
            </button>
          )}
          {providers.emailOTP && view === 'main' && !signupBlocked && !isSignup && (
            <button type="button" onClick={() => switchView('otp')} className="w-full text-xs font-semibold text-blue-700 hover:underline">
              Sign in with an email code instead
            </button>
          )}

          {isSignup && !signupBlocked && (
            <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-blue-500/5 border border-slate-200 dark:border-blue-500/10 text-left">
              <SafeIcon icon={FiShield} className="text-blue-600 dark:text-blue-500 w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                By creating an account, you agree to our terms of service and usage limits for free tier accounts.
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 dark:text-slate-500 font-medium">
          {isSignup ? (
            <>
              Already have an account? <Link to="/login" className="text-blue-700 font-semibold hover:underline">Log In</Link>
            </>
          ) : providers.public_signup === false ? (
            'Public registration is restricted by the administrator.'
          ) : (
            <>
              Don't have an account? <Link to="/register" className="text-blue-700 font-semibold hover:underline">Create Account</Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthCard;
